import { useTranslation } from 'react-i18next';
import { Plus, Star, Trash2 } from 'lucide-react';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { emptyGuardian, type GuardianDraft } from './kids-area';

interface GuardianFieldsProps {
  guardians: GuardianDraft[];
  onChange: (guardians: GuardianDraft[]) => void;
  /** Below one the check-in is refused server-side, so the last row cannot go. */
  minimum?: number;
}

/**
 * The adults a child may be handed back to.
 *
 * A list rather than a single field, and that is the whole point of the new
 * model: `ChildGuardian` is many-to-many because a child usually has two, and
 * the old one-guardian link is what made a father need a manager's signature to
 * collect his own child. Capturing both here is what stops that happening at the
 * counter later.
 *
 * The phone number is required and is not decoration: it is the guardian's
 * identity (`@@unique([tenantId, phone])`), so entering a mother who is already
 * on another child's record updates her rather than creating a second copy of a
 * number the desk will one day ring. `backupPhone` is the number to try when the
 * first does not answer, which is a real thing a kids desk needs at 9pm.
 */
export function GuardianFields({ guardians, onChange, minimum = 1 }: GuardianFieldsProps) {
  const { t } = useTranslation();

  const patch = (index: number, next: Partial<GuardianDraft>) =>
    onChange(guardians.map((row, i) => (i === index ? { ...row, ...next } : row)));

  /** Exactly one primary: it is "the number to try first", not a flag per row. */
  const setPrimary = (index: number) =>
    onChange(guardians.map((row, i) => ({ ...row, isPrimary: i === index })));

  return (
    <div className="space-y-3">
      {guardians.map((guardian, index) => (
        <div key={index} className="space-y-2 rounded-lg border border-dashed p-3">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-medium">
              {t('spaces.kids.guardianN', { number: index + 1 })}
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setPrimary(index)}
                aria-pressed={guardian.isPrimary}
                className={
                  guardian.isPrimary
                    ? 'flex items-center gap-1 text-xs font-medium text-accent'
                    : 'flex items-center gap-1 text-xs text-ink-muted hover:underline'
                }
              >
                <Star className="h-3.5 w-3.5" aria-hidden />
                {t('spaces.kids.callFirst')}
              </button>
              {guardians.length > minimum && (
                <button
                  type="button"
                  onClick={() => onChange(guardians.filter((_, i) => i !== index))}
                  className="flex items-center gap-1 text-xs text-ink-muted hover:underline"
                >
                  <Trash2 className="h-3.5 w-3.5" aria-hidden />
                  {t('common.delete')}
                </button>
              )}
            </div>
          </div>

          <div className="grid gap-2 sm:grid-cols-2">
            <Field label={t('spaces.kids.guardianName')} required>
              <Input
                value={guardian.name}
                onChange={(event) => patch(index, { name: event.currentTarget.value })}
                placeholder={t('spaces.kids.guardianNamePlaceholder')}
              />
            </Field>
            <Field label={t('spaces.kids.phone')} hint={t('spaces.kids.phoneHint')} required>
              <Input
                type="tel"
                dir="ltr"
                value={guardian.phone}
                onChange={(event) => patch(index, { phone: event.currentTarget.value })}
                placeholder="+9665…"
              />
            </Field>
            <Field label={t('spaces.kids.backupPhone')} hint={t('spaces.kids.backupPhoneHint')}>
              <Input
                type="tel"
                dir="ltr"
                value={guardian.backupPhone}
                onChange={(event) => patch(index, { backupPhone: event.currentTarget.value })}
                placeholder="+9665…"
              />
            </Field>
            <Field label={t('spaces.kids.relationship')}>
              <Input
                value={guardian.relationship}
                onChange={(event) => patch(index, { relationship: event.currentTarget.value })}
                placeholder={t('spaces.kids.relationshipPlaceholder')}
              />
            </Field>
          </div>
        </div>
      ))}

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => onChange([...guardians, { ...emptyGuardian }])}
      >
        <Plus className="h-4 w-4" />
        {t('spaces.kids.addGuardian')}
      </Button>
    </div>
  );
}
